import { Heading } from 'components/1-atoms/Heading';
import { Table, TableCell, TableData } from 'components/2-molecules/Table';
import { useCallback, useEffect, useState } from 'react';
import { formatDate } from 'utilities/date';
import { convertNumberToCurrency } from 'utilities/sanitize';
import { ConditionStepProps } from '../ConditionStep';
import { TravelDeductionFormData, TravelDeductionProps } from '../TravelDeduction';
import { rates } from '../rates';
import styles from './ResultStep.module.scss';

export interface ResultStepProps {
	result: {
		description: string;
		perDayTitle: string;
		totalTitle: string;
		helperTexts: {
			daysWithTransport: string;
			deductionForThePeriod: string;
			theFirst: string;
		};
	};
	formData: TravelDeductionFormData;
	units: TravelDeductionProps['units'];
	bridges: ConditionStepProps['paymentBridges']['options'];
}

export const ResultStep = ({ result, formData, units, bridges }: ResultStepProps) => {
	const { helperTexts, description, perDayTitle, totalTitle } = result ?? {};
	const {
		dateStart,
		dateEnd,
		distance: distanceString,
		specialAddress,
		paymentBridgeUsage,
		paymentBridges: choosenPaymentBridges,
		paymentBridgeBothWays,
		transportType,
		workingDaysWithTransport,
	} = formData ?? {};
	const distance = Number(distanceString);
	const { kilometers } = units ?? {};
	const rate = rates[formData?.year];

	const [deductionOver24, setDeductionOver24] = useState<number>(null);
	const [deductionOver120, setDeductionOver120] = useState<number>(null);
	const [totalBridgeDeduction, setTotalBridgeDeduction] = useState<number>(null);
	const [tableData, setTableData] = useState(null);

	const getDeductionOver24 = useCallback((): TableCell[] => {
		const conditionalRate = rate[specialAddress === 'true' ? '24-120specialAddress' : '24-120'];
		const restrictedDistance = Math.min(distance, 120);
		const deductionValue = (restrictedDistance - 24) * conditionalRate;
		const deductionCurrencyValue = convertNumberToCurrency(deductionValue);
		setDeductionOver24(Number(deductionValue));
		const label = { text: `25 - ${restrictedDistance} ${kilometers}` };
		const value = { text: deductionCurrencyValue };
		return [label, value];
	}, [distance, kilometers, rate, specialAddress]);

	const getDeductionOver120 = useCallback((): TableCell[] => {
		const conditionalRate = rate[specialAddress === 'true' ? '120+specialAddress' : '120+'];
		const deductionValue = (distance - 120) * conditionalRate;
		const deductionCurrencyValue = convertNumberToCurrency(deductionValue);
		setDeductionOver120(deductionValue);
		const label = { text: `121 - ${distance} ${kilometers}` };
		const value = { text: deductionCurrencyValue };
		return [label, value];
	}, [distance, kilometers, rate, specialAddress]);

	const getBridgeDeduction = useCallback((): TableCell[][] => {
		if (!choosenPaymentBridges) return [];
		const bridgeDeductions: number[] = [];
		const data: TableCell[][] = choosenPaymentBridges?.map((bridge) => {
			let deductionValue: number = rate.bridges[bridge][transportType];
			if (paymentBridgeBothWays === 'true') deductionValue *= 2;
			bridgeDeductions.push(deductionValue);
			const label = { text: bridges?.find((paymentBridge) => paymentBridge?.value === bridge)?.label };
			const value = { text: convertNumberToCurrency(deductionValue) };
			return [label, value];
		});
		bridgeDeductions.length && setTotalBridgeDeduction(bridgeDeductions?.reduce((accumulator, currentValue) => accumulator + currentValue, 0));
		return data;
	}, [bridges, choosenPaymentBridges, paymentBridgeBothWays, rate.bridges, transportType]);

	useEffect(() => {
		const deductionPerDay = deductionOver24 + deductionOver120 + totalBridgeDeduction;
		const deductionCurrencyPerDay = convertNumberToCurrency(deductionPerDay);
		const totalDeductionPerDayData: TableCell[] = [{ text: totalTitle }, { text: deductionCurrencyPerDay }];

		const perDayData = [
			[{ text: helperTexts?.theFirst + ' 24 ' + kilometers }, { text: convertNumberToCurrency(0) }],
			distance > 24 ? getDeductionOver24() : [],
			distance > 120 ? getDeductionOver120() : [],
		]
			.concat(paymentBridgeUsage === 'true' ? getBridgeDeduction() : [])
			.concat([totalDeductionPerDayData]);

		const total: TableCell[] = [
			{ text: `${workingDaysWithTransport} ${helperTexts?.daysWithTransport.toLowerCase()} × ${deductionCurrencyPerDay} ` },
			{ text: convertNumberToCurrency(deductionPerDay * Number(workingDaysWithTransport)) },
		];

		const tableData: TableData[] = [
			{
				tableHeaders: [perDayTitle],
				tableRows: perDayData,
			},
			{
				tableHeaders: [totalTitle],
				tableRows: [total],
			},
		];
		setTableData(tableData);
	}, [
		deductionOver120,
		deductionOver24,
		distance,
		getBridgeDeduction,
		getDeductionOver120,
		getDeductionOver24,
		helperTexts?.daysWithTransport,
		helperTexts?.theFirst,
		kilometers,
		paymentBridgeUsage,
		perDayTitle,
		totalBridgeDeduction,
		totalTitle,
		workingDaysWithTransport,
	]);

	return (
		<div className={styles.ResultStep}>
			<Heading className={styles.ResultStep_title} headingLevel="h2">
				{helperTexts?.deductionForThePeriod} {formatDate(dateStart, 'dd/mm/yyyy')} - {formatDate(dateEnd, 'dd/mm/yyyy')}
			</Heading>
			<p className={styles.ResultStep_description}>{description}</p>
			<Table simple={false} tableData={tableData} />
		</div>
	);
};
